import { z } from 'zod';
import { LibraryGroupIdSchema, TemplateIdSchema } from './templates';
import { CoachCopilotContextSchema } from './copilotContext';

/** Surveys a journey can ask. The app owns where each one lives (its PostHog survey). */
export const COACH_SURVEYS = {
  'onboarding-tour': { label: 'How was your onboarding tour experience?' },
} as const satisfies Record<string, { label: string }>;

export type CoachSurveyId = keyof typeof COACH_SURVEYS;

export const COACH_SURVEY_IDS = Object.keys(COACH_SURVEYS) as CoachSurveyId[];

const SurveyIdSchema = z.enum(COACH_SURVEY_IDS as [CoachSurveyId, ...CoachSurveyId[]]);

/**
 * What a step can do, not just say: change the board, or work the Copilot or a chat.
 *
 * A step names an action and its params; the app does the work through the code the product
 * already uses (board edits go through the same operations as the Copilot's, so each is one
 * step of the person's undo). Journeys never import that code, and the app must implement
 * every action listed here (its implementation map is typed against this catalog).
 *
 * - `refs` are params that name a context key rather than hold a value: the engine reads
 *   them from the journey's context when the step runs (`chat: 'chatNodeId'`).
 * - Other string params may use `{{key}}` to put a context value into text
 *   (`'Write 3 hooks in the style of {{creatorUrl}}'`).
 * - `returns` are what a successful run gives back; a step's `bind` keeps them in context.
 */

/** A context key a param reads from. */
export const ContextRefSchema = z.string().regex(/^[a-z][a-zA-Z0-9]*$/, 'camelCase context key');

const Text = z.string().min(1);

/** A place on the board, in board coordinates. */
const PointSchema = z.object({ x: z.number(), y: z.number() }).strict();

/** Things a step can make start out of sight, for a later step to `board.reveal`. */
const Hidden = z.boolean().optional();

export interface CoachActionDefinition {
  label: string;
  params: z.ZodType;
  refs: readonly string[];
  returns: readonly string[];
}

export const COACH_ACTIONS = {
  'discover.open': {
    label: 'Open the current search in Discover',
    params: z.object({}).strict(),
    refs: [],
    returns: [],
  },
  // ── board ────────────────────────────────────────────────────────────────
  'board.seed': {
    label: 'Lay out a template on the board',
    params: z.object({ template: TemplateIdSchema }).strict(),
    refs: [],
    returns: ['chatNodeId', 'groupNodeId'],
  },
  'board.add-group': {
    label: "Copy a finished group from Poppy's template boards onto the board",
    params: z
      .object({
        group: LibraryGroupIdSchema.optional(),
        /** A context key holding the library group's id (a choice made earlier). */
        groupFrom: ContextRefSchema.optional(),
        connectTo: ContextRefSchema.optional(),
        /** Where its top left goes; left of everything on the board when not given. */
        at: PointSchema.optional(),
        hidden: Hidden,
      })
      .strict()
      .refine(params => Boolean(params.group) !== Boolean(params.groupFrom), {
        message: 'give exactly one of group or groupFrom',
      }),
    refs: ['groupFrom', 'connectTo'],
    returns: ['groupNodeId'],
  },
  'board.add-link': {
    label: 'Add a link (video, post, profile, website)',
    params: z
      .object({
        url: z.string().url().optional(),
        urlFrom: ContextRefSchema.optional(),
        /** A group already on the board to add into. */
        group: ContextRefSchema.optional(),
        /** A new group to put it in. */
        groupTitle: Text.optional(),
        connectTo: ContextRefSchema.optional(),
      })
      .strict()
      .refine(params => Boolean(params.url) !== Boolean(params.urlFrom), {
        message: 'give exactly one of url or urlFrom',
      }),
    refs: ['urlFrom', 'group', 'connectTo'],
    returns: ['nodeId', 'groupNodeId'],
  },
  'board.add-note': {
    label: 'Add a note',
    params: z
      .object({
        title: Text,
        text: Text.optional(),
        textFrom: ContextRefSchema.optional(),
        group: ContextRefSchema.optional(),
        groupTitle: Text.optional(),
        connectTo: ContextRefSchema.optional(),
      })
      .strict()
      .refine(params => Boolean(params.text) !== Boolean(params.textFrom), {
        message: 'give exactly one of text or textFrom',
      }),
    refs: ['textFrom', 'group', 'connectTo'],
    returns: ['nodeId', 'groupNodeId'],
  },
  'board.add-chat': {
    label: 'Add a chat',
    params: z
      .object({
        title: Text.optional(),
        connect: z.array(ContextRefSchema).default([]),
        at: PointSchema.optional(),
        hidden: Hidden,
      })
      .strict(),
    refs: ['connect'],
    returns: ['chatNodeId'],
  },
  'brand.await-text': {
    label: "Wait until an item on the brand's board has its text saved (a recording's transcript)",
    params: z.object({ brand: ContextRefSchema, node: ContextRefSchema }).strict(),
    refs: ['brand', 'node'],
    returns: [],
  },
  'board.add-brand-card': {
    label: "Add a brand profile's card, wired from its groups, and build its profile from them",
    params: z
      .object({ brand: ContextRefSchema, connect: z.array(ContextRefSchema).default([]) })
      .strict(),
    refs: ['brand', 'connect'],
    returns: ['cardNodeId'],
  },
  'board.connect': {
    label: 'Connect things to a chat',
    params: z.object({ nodes: z.array(ContextRefSchema).min(1), chat: ContextRefSchema }).strict(),
    refs: ['nodes', 'chat'],
    returns: [],
  },
  'board.group': {
    label: 'Group things',
    params: z.object({ nodes: z.array(ContextRefSchema).min(2), title: Text }).strict(),
    refs: ['nodes'],
    returns: ['groupNodeId'],
  },
  'board.add-slot': {
    label: 'Add a placeholder card for something the person will add (an answer, a file)',
    params: z
      .object({
        title: Text,
        /** A note kept under what fills the card (its label on the board). */
        note: Text.optional(),
        at: PointSchema,
        size: z
          .object({ width: z.number().positive(), height: z.number().positive() })
          .strict()
          .optional(),
      })
      .strict(),
    refs: [],
    returns: ['nodeId'],
  },
  'board.fill-slot': {
    label: 'Put what the person added where its placeholder card was, and retire the card',
    params: z.object({ slot: ContextRefSchema, node: ContextRefSchema }).strict(),
    refs: ['slot', 'node'],
    returns: [],
  },
  'board.reveal': {
    label: 'Show things made hidden, with everything inside them',
    params: z.object({ nodes: z.array(ContextRefSchema).min(1) }).strict(),
    refs: ['nodes'],
    returns: [],
  },
  'board.remove': {
    label: 'Remove things from the board (placeholder cards left unanswered, say)',
    params: z.object({ nodes: z.array(ContextRefSchema).min(1) }).strict(),
    refs: ['nodes'],
    returns: [],
  },
  'board.show': {
    label: 'Bring things into view',
    params: z.object({ nodes: z.array(ContextRefSchema).min(1) }).strict(),
    refs: ['nodes'],
    returns: [],
  },
  'board.place': {
    label: 'Put something already on the board under or beside something else',
    params: z
      .object({
        node: ContextRefSchema,
        /** Directly under this, left edges lined up. */
        below: ContextRefSchema.optional(),
        /** To the right of this. */
        rightOf: ContextRefSchema.optional(),
        /** Vertically centred on the span from the first to the last of these. */
        centerBetween: z.tuple([ContextRefSchema, ContextRefSchema]).optional(),
        /** Made as wide as this. */
        matchWidth: ContextRefSchema.optional(),
        /** Space kept from what it sits under or beside; the board's usual gap otherwise. */
        gap: z.number().int().min(0).max(2000).optional(),
      })
      .strict()
      .refine(params => Boolean(params.below) !== Boolean(params.rightOf), {
        message: 'give exactly one of below or rightOf',
      }),
    refs: ['node', 'below', 'rightOf', 'centerBetween', 'matchWidth'],
    returns: [],
  },
  'board.add-empty-group': {
    label: 'Make an empty group for the person (or the Copilot) to fill',
    params: z.object({ title: Text, at: PointSchema.optional() }).strict(),
    refs: [],
    returns: ['groupNodeId'],
  },
  'board.move-to-group': {
    label: 'Move things already on the board into a group',
    params: z.object({ nodes: z.array(ContextRefSchema).min(1), group: ContextRefSchema }).strict(),
    refs: ['nodes', 'group'],
    returns: [],
  },
  'board.create': {
    label: 'Make a new, empty board for the person (the journey can open it next)',
    params: z.object({ name: Text }).strict(),
    refs: [],
    returns: ['newBoardId'],
  },
  'board.open': {
    label:
      "Go to another board. The journey's next step waits for `board.entered` and binds `boardId`.",
    params: z.object({ board: ContextRefSchema }).strict(),
    refs: ['board'],
    returns: [],
  },

  // ── brands ───────────────────────────────────────────────────────────────
  'brand.create': {
    label: 'Make a brand profile with its own board (My Business, My Voice and Content)',
    params: z.object({ name: Text }).strict(),
    refs: [],
    returns: ['brandId', 'brandBoardId', 'businessGroupId', 'voiceGroupId'],
  },
  'brand.set-default': {
    label: 'Make a brand the default one every chat writes as',
    params: z.object({ brand: ContextRefSchema }).strict(),
    refs: ['brand'],
    returns: [],
  },
  'brand.summary': {
    label: "Read back everything on a brand's board as text, for a card or a message",
    params: z.object({ brand: ContextRefSchema }).strict(),
    refs: ['brand'],
    returns: ['brandSummary'],
  },

  'project.open': {
    label: 'Open a post or its video editor',
    params: z
      .object({
        project: ContextRefSchema,
        view: z.enum(['post', 'video']).optional(),
        delivery: z.enum(['post', 'plan']).optional(),
      })
      .strict(),
    refs: ['project'],
    returns: [],
  },
  'studio.calendar': {
    label: 'Return to the Content Studio calendar',
    params: z.object({}).strict(),
    refs: [],
    returns: [],
  },

  // ── the app ──────────────────────────────────────────────────────────────
  'app.celebrate': {
    label: 'Celebrate: confetti across the screen',
    params: z.object({}).strict(),
    refs: [],
    returns: [],
  },
  'app.survey': {
    label: 'Ask the person a short survey',
    params: z.object({ survey: SurveyIdSchema }).strict(),
    refs: [],
    returns: [],
  },

  // ── Copilot ──────────────────────────────────────────────────────────────
  'copilot.open': {
    label: 'Open the Copilot',
    params: z.object({}).strict(),
    refs: [],
    returns: [],
  },
  'copilot.fill': {
    label: 'Open the Copilot with a message typed in, for the person to send',
    params: z
      .object({
        prompt: Text,
        /** Hidden task data that goes with the message if the person sends it. */
        context: CoachCopilotContextSchema.optional(),
      })
      .strict(),
    refs: [],
    returns: [],
  },
  'copilot.ask': {
    label: 'Send the Copilot a message',
    params: z
      .object({
        prompt: Text,
        /**
         * Standing instructions that travel with the message but never show in the chat: how
         * the Copilot should run this part of the journey (the questions to ask, what to put
         * where). They stay with the conversation, so every later turn still follows them.
         */
        instructions: Text.optional(),
        label: z.string().trim().min(1).max(300).optional(),
        context: CoachCopilotContextSchema.optional(),
      })
      .strict(),
    refs: [],
    returns: [],
  },

  // ── board chats ──────────────────────────────────────────────────────────
  'chat.fill': {
    label: 'Type a message into a board chat, for the person to send',
    params: z.object({ chat: ContextRefSchema, prompt: Text }).strict(),
    refs: ['chat'],
    returns: [],
  },
  'chat.ask': {
    label: 'Send a board chat a message',
    params: z.object({ chat: ContextRefSchema, prompt: Text }).strict(),
    refs: ['chat'],
    returns: [],
  },
} as const satisfies Record<string, CoachActionDefinition>;

export type CoachActionId = keyof typeof COACH_ACTIONS;

export const COACH_ACTION_IDS = Object.keys(COACH_ACTIONS) as CoachActionId[];

/** An action's params as a journey writes them. */
export type CoachActionParams<A extends CoachActionId> = z.output<
  (typeof COACH_ACTIONS)[A]['params']
>;

/** What an action gives back, by the names in its `returns`. */
export type CoachActionReturns<A extends CoachActionId> = Partial<
  Record<(typeof COACH_ACTIONS)[A]['returns'][number], string>
>;

/** How a run of an action went. */
export type CoachActionOutcome<A extends CoachActionId = CoachActionId> =
  | { ok: true; result: CoachActionReturns<A> }
  | { ok: false; error: string };
